// @ts-strict-ignore
import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { NAME_VALIDATION_PATTERN } from './createNameSchema'

const MIN_LENGTH = 2

const ADDRESS_LINE_MAX_LENGTH = 35
const ADDRESS_FIELDS_REQUIRED_TOGETHER = [
  'addressLine1',
  'addressLine2',
  'city',
  'postCode',
  'region',
]

interface FieldValidationProps {
  value: string
  ctx: yup.TestContext<yup.AnyObject>
  label: string
  t: TFunction<'translation', undefined>
  minLength?: number
  maxLength?: number
}

interface AddressFieldValidationProps extends FieldValidationProps {
  fieldType: 'beneficiary' | 'bank' | 'intermediaryBank'
}

interface CreateBeneficiaryAddressSchemaProps {
  label: string
  t: TFunction<'translation', undefined>
  minLength?: number
  maxLength?: number
}

const isValidLength = ({ value, ctx, label, t, minLength, maxLength }: FieldValidationProps) => {
  const minimumLength = minLength ?? MIN_LENGTH
  const maximumLength = maxLength ?? ADDRESS_LINE_MAX_LENGTH

  if (value?.length < minimumLength) {
    return ctx.createError({
      message: t?.('fieldMinLength', {
        field: label,
        min: minimumLength,
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (value?.length > maximumLength) {
    return ctx.createError({
      message: t?.('fieldMaxLength', {
        field: label,
        max: maximumLength,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

const validateAddressField = ({
  value,
  ctx,
  label,
  t,
  minLength,
  maxLength,
  fieldType,
}: AddressFieldValidationProps) => {
  const formField = ctx?.path

  // Determine if this is address line 1 or 2 based on field type
  let isAddressLine1: boolean
  let isAddressLine2: boolean

  if (fieldType === 'beneficiary') {
    isAddressLine1 = formField === 'addressLine1'
    isAddressLine2 = formField === 'addressLine2'
  } else if (fieldType === 'intermediaryBank') {
    isAddressLine1 = formField === 'intermediaryBankAddressLine1'
    isAddressLine2 = formField === 'intermediaryBankAddressLine2'
  } else if (fieldType === 'bank') {
    // bank
    isAddressLine1 = formField === 'bankAddressLine1'
    isAddressLine2 = formField === 'bankAddressLine2'
  }

  // If validating bank address fields, or intermediary bank address fields,
  // only the address line 2 is not required
  // If any fields have value they are validate for length and special characters
  const isBankOrIntermediaryBankAddressField =
    fieldType === 'bank' || fieldType === 'intermediaryBank'
  if (isBankOrIntermediaryBankAddressField) {
    if (!value) {
      if (isAddressLine2) {
        return true
      }

      ctx.schema.required()
      return ctx.createError({
        message: t?.('fieldIsARequiredField', {
          field: label,
          interpolation: { escapeValue: false },
        }),
      })
    }

    if (!NAME_VALIDATION_PATTERN.test(value)) {
      return ctx.createError({
        message: t?.('manageBeneficiaries.nameFieldInvalidCharacters'),
      })
    }

    return isValidLength({
      value,
      ctx,
      label,
      t,
      minLength,
      maxLength: isAddressLine1 || isAddressLine2 ? ADDRESS_LINE_MAX_LENGTH : maxLength,
    })
  }

  // If validating beneficiary address fields,
  // 1. Address line 2 is not required
  // 2. If any field has a value, then all other fields are required, except address line 2
  // 3. If no fields have a value, then none are required
  // 4. ONLY if USD ACH or Fedwire is selected, then address fields are required, (except address line 2)
  const { transferDestination, currencyCode, beneficiaryPaymentMethod } = ctx?.parent || {}

  const otherFields = ADDRESS_FIELDS_REQUIRED_TOGETHER?.filter(field => field !== ctx.path)

  const isRequired =
    !isAddressLine2 && otherFields?.map(field => ctx?.parent?.[field]).some(field => !!field)

  if (transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
    if (!value) {
      if (isAddressLine2) {
        return true
      }

      ctx.schema.required()
      return ctx.createError({
        message: t?.('fieldIsARequiredField', {
          field: label,
          interpolation: { escapeValue: false },
        }),
      })
    }
    if (!NAME_VALIDATION_PATTERN.test(value)) {
      return ctx.createError({
        message: t?.('manageBeneficiaries.nameFieldInvalidCharacters'),
      })
    }

    return isValidLength({
      value,
      ctx,
      label,
      t,
      minLength,
      maxLength: isAddressLine1 || isAddressLine2 ? ADDRESS_LINE_MAX_LENGTH : maxLength,
    })
  }

  const isUsdAchOrFedwire =
    currencyCode === BeneficiaryDetailsCurrency.USD &&
    beneficiaryPaymentMethod !== USDBeneficiaryOptions.CUBIX

  if (isUsdAchOrFedwire) {
    if (!value) {
      if (isAddressLine2) {
        return true
      }

      ctx.schema.required()
      return ctx.createError({
        message: t?.('fieldIsARequiredField', {
          field: label,
          interpolation: { escapeValue: false },
        }),
      })
    }

    return isValidLength({
      value,
      ctx,
      label,
      t,
      minLength,
      maxLength: isAddressLine1 || isAddressLine2 ? ADDRESS_LINE_MAX_LENGTH : maxLength,
    })
  }

  if (!value) {
    if (isRequired) {
      ctx.schema.required()
      return ctx.createError({
        message: t?.('partialAddressError'),
      })
    }

    ctx.schema.notRequired()
    return true
  }

  if (!NAME_VALIDATION_PATTERN.test(value)) {
    return ctx.createError({
      message: t?.('manageBeneficiaries.nameFieldInvalidCharacters'),
    })
  }

  return isValidLength({ value, ctx, label, t, minLength, maxLength })
}

export const createBeneficiaryAddressSchema = ({
  label,
  t,
  minLength,
  maxLength,
}: CreateBeneficiaryAddressSchemaProps) =>
  yup
    .string()
    .trim()
    .label(label)
    .test({
      test(value, ctx) {
        return validateAddressField({
          value,
          ctx,
          label,
          t,
          minLength,
          maxLength,
          fieldType: 'beneficiary',
        })
      },
    })

export const createBankAddressSchema = (
  label: string,
  t: TFunction<'translation', undefined>,
  isIntermediaryBank: boolean = false
) =>
  yup
    .string()
    .trim()
    .label(label)
    .test({
      test(value, ctx) {
        return validateAddressField({
          value,
          ctx,
          label,
          t,
          fieldType: isIntermediaryBank ? 'intermediaryBank' : 'bank',
        })
      },
    })
